import { useCallback, useEffect, useMemo, useRef } from "react";
import { TaskConversationComposer } from "@/components/panels/task-conversation-composer";
import { useFrameActions } from "@/components/shapes/shared/use-frame-actions";
import { useFrameRenderer } from "@/components/shapes/shared/frame-renderer-context";
import { useFrameSurface } from "@/components/shapes/shared/use-frame-surface";
import {
  latestMessageScrollKey,
  useAutoScrollToLatest,
} from "@/hooks/use-auto-scroll-to-latest";
import { useCursorInputContext } from "@/components/layout/cursor-input-context";
import { frameTaskChat } from "@/lib/task-chat-kind";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { framesConversationMessages } from "@/i18n/messages/frames-conversation";
import { conversationReadOnlyReasonText } from "@/lib/conversation-read-only-reason";
import { ConversationFrameThread } from "./conversation-frame-thread";
import { ConversationFrameActions } from "./conversation-frame-actions";
import { ConversationFrameSubmitPending } from "./conversation-system-message";
import { handleConversationFrameLink } from "./conversation-frame-links";
import {
  conversationFrameContextMetrics,
  conversationFrameNativeGroup,
  conversationFrameTaskId,
  metadataString,
} from "./conversation-frame-utils";
import { useConversationFrameMessages } from "./use-conversation-frame-messages";
import { useConversationFrameNewChat } from "./use-conversation-frame-new-chat";
import { useConversationFrameAgentRouting } from "./use-conversation-frame-agent-routing";
import { useConversationFrameSubmit } from "./use-conversation-frame-submit";
import { useConversationFramePendingSubmit } from "./use-conversation-frame-pending-submit";
import { useConversationFrameRouting } from "./use-conversation-frame-routing";
import { useConversationFrameRunningState } from "./use-conversation-frame-running-state";
import { useConversationFrameModel } from "./use-conversation-frame-model";
import { useConversationFrameDraft } from "./use-conversation-frame-draft";
import type { ConversationFrameProps } from "./conversation-frame-types";
import { useConversationFrameInputExpansion } from "./use-conversation-frame-input-expansion";
export function ConversationFrame(props: ConversationFrameProps) {
  const { id, data, selected, detached } = props;
  const t = useT(framesConversationMessages);
  const renderer = useFrameRenderer();
  const independentSurface =
    Boolean(detached) || renderer.independentSurface === true;
  const inheritedInputContext = useCursorInputContext();
  const ctx = detached?.inputContext ?? inheritedInputContext;
  // A conversation is glass everywhere, like the summoned chat: in a focus
  // or floating window too, where its plate follows the DOM, not the canvas.
  const frameSurface = useFrameSurface(id, {
    nativeGroup: conversationFrameNativeGroup(
      detached?.nativeSurfaceId ?? id,
      independentSurface,
    ),
    glass: true,
  });
  const composerId = `conversation-frame:${detached?.submitFrameId ?? id}`;
  const { draft, onChange: handleDraftChange } =
    useConversationFrameDraft(composerId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const { agentName: fallbackAgentName } = useFrameActions(
    detached?.actionFrameId ?? id,
  );
  const metadata = data.metadata;
  const baseConversationId = metadataString(
    metadata,
    "conversationId",
    "conversation_id",
  );
  const executionId = metadataString(metadata, "executionId", "execution_id");
  const runtimeSessionId = metadataString(
    metadata,
    "runtimeSessionId",
    "liveSessionId",
  );
  const liveExecutionId = metadataString(metadata, "liveExecutionId");
  const projectAgentId = metadataString(
    metadata,
    "projectAgentId",
    "project_agent_id",
  );
  const agentKey = metadataString(metadata, "agentKey", "agent_key");
  const taskId = conversationFrameTaskId(data);
  const taskChat = frameTaskChat(metadata);
  const persistedContext = useMemo(
    () => conversationFrameContextMetrics(metadata),
    [metadata],
  );
  const canStartNewConversation =
    metadata.conversationCanStartNewConversation !== false;
  const conversationReadOnlyReason = conversationReadOnlyReasonText(
    metadataString(metadata, "conversationReadOnlyReason"),
    metadataString(metadata, "conversationReadOnlyProject"),
  );
  const matchingExecutionId = executionId ?? liveExecutionId;
  const { conversationController: routingController, view } =
    useConversationFrameRouting(
      baseConversationId,
      matchingExecutionId,
      canStartNewConversation,
      id,
    );
  const conversationReadOnly =
    metadata.conversationReadOnly === true &&
    !(canStartNewConversation && routingController.startNewConversation);
  const isNewPending = view === "new-pending";
  const agentName =
    metadataString(metadata, "agentName", "agent_name") ?? fallbackAgentName;
  const {
    focusedAgent: routedFocusedAgent,
    frameAgent,
    frameSelectedAgentIds,
    resetFrameSelectedAgent,
    setFrameSelectedAgentIds,
  } = useConversationFrameAgentRouting({
    ctx,
    frameId: id,
    projectAgentId,
    agentKey,
    agentName,
  });
  const { conversationController, createConversation, creating } =
    useConversationFrameNewChat(
      routingController,
      detached?.submitFrameId ?? id,
      ctx?.inputAgents.find((agent) => agent.name.toLowerCase() === "denker")
        ?.id ?? projectAgentId,
    );
  const {
    focusedAgent: modelAgent,
    onUpdateAgentModel,
    onUpdateAgentEffort,
  } = useConversationFrameModel(
    routedFocusedAgent,
    metadata,
    baseConversationId,
    isNewPending,
    ctx?.onUpdateAgentModel,
    ctx?.onUpdateAgentEffort,
    detached?.submitFrameId ?? id,
  );
  const focusedAgent = modelAgent
    ? {
        ...modelAgent,
        runtimeSessionId: runtimeSessionId ?? modelAgent.runtimeSessionId,
        conversationId: baseConversationId,
        spaceId: data.spaceId ?? modelAgent.spaceId,
        agentName: agentName ?? modelAgent.agentName,
        ...persistedContext,
      }
    : null;
  const { displayedFrameMessages, showLoadingPlaceholder } =
    useConversationFrameMessages({
      frameId: id,
      conversationId: baseConversationId,
      executionId: matchingExecutionId,
      runtimeSessionId,
      requireExactIdentity: metadata.conversationRequiresExactIdentity === true,
      detached,
      isNewPending,
    });
  const { isRunning, runningExecutionIds, onStop } =
    useConversationFrameRunningState({
      detachedExecutionId: isNewPending ? undefined : executionId,
      detachedIsRunning: isNewPending ? false : detached?.isRunning,
      executionId: isNewPending ? undefined : matchingExecutionId,
      preferDetachedRunning: !!detached && !isNewPending,
      conversationId: isNewPending ? undefined : baseConversationId,
      runtimeSessionId: isNewPending ? undefined : runtimeSessionId,
    });
  const threadScrollKey = useMemo(
    () =>
      latestMessageScrollKey(
        displayedFrameMessages,
        `${isRunning}:${isNewPending}`,
      ),
    [displayedFrameMessages, isNewPending, isRunning],
  );
  const { onScroll: handleThreadScroll } = useAutoScrollToLatest(
    scrollRef,
    threadScrollKey,
  );
  const handleSubmit = useConversationFrameSubmit({
    taskChat,
    ctx,
    draft,
    frameId: detached?.submitFrameId ?? id,
    conversationFrameId: detached?.submitFrameId ?? id,
    conversationId: conversationController.startNewConversation
      ? undefined
      : baseConversationId,
    agentKey,
    projectAgentId,
    frameAgent,
    selectedAgentIds: frameSelectedAgentIds,
    resetSelectedAgent: resetFrameSelectedAgent,
    executionId: conversationController.startNewConversation
      ? undefined
      : executionId,
    startNewConversation: conversationController.startNewConversation,
    createConversation:
      canStartNewConversation && !taskChat ? createConversation : undefined,
  });
  const { submit: handlePendingSubmit, pending: submitPending } =
    useConversationFramePendingSubmit({
      onSubmit: handleSubmit,
    });
  const handleSuggestionSend = useCallback(
    (text: string) => {
      if (!ctx || conversationController.startNewConversation) return;
      void ctx.onSubmit(text, "denker", {
        conversationId: baseConversationId,
        conversationFrameId: detached?.submitFrameId ?? id,
        composerId,
      });
    },
    [
      ctx,
      conversationController.startNewConversation,
      baseConversationId,
      detached,
      id,
      composerId,
    ],
  );
  const handleCompactExpansionDeltaChange = useConversationFrameInputExpansion(
    id,
    independentSurface,
  );
  useEffect(() => {
    const frame = frameRef.current;
    const input = inputRef.current;
    if (!frame || !input) return;
    const updateClearance = () => {
      const height = input.getBoundingClientRect().height;
      frame.style.setProperty(
        "--conversation-composer-clearance",
        `${Math.max(96, Math.ceil(height) + 24)}px`,
      );
    };
    updateClearance();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateClearance);
    observer.observe(input);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={frameRef}
      className={cn(
        frameSurface.className,
        "relative flex h-full min-w-0 flex-col !overflow-visible text-primary",
        selected
          ? "border-[var(--frame-selected-stroke)]"
          : "border-glass-stroke",
      )}
      data-testid="conversation-frame"
      data-conversation-frame-id={id}
      {...frameSurface.attributes}
    >
      {!independentSurface && (
        <ConversationFrameActions frameId={id} frame={data} />
      )}

      <ConversationFrameThread
        messages={displayedFrameMessages}
        scrollRef={scrollRef}
        onScroll={handleThreadScroll}
        showLoadingPlaceholder={showLoadingPlaceholder}
        isRunning={isRunning}
        newChatPending={isNewPending}
        runningExecutionIds={runningExecutionIds}
        onLinkClick={handleConversationFrameLink}
        onSuggestionSend={handleSuggestionSend}
        suggestionProjectId={ctx?.projectId}
        conversationFrameId={id}
      />

      <div
        ref={inputRef}
        className="nodrag nowheel min-w-0"
        data-testid="conversation-frame-input"
      >
        {submitPending && <ConversationFrameSubmitPending />}
        <TaskConversationComposer
          taskChat={taskChat}
          taskId={taskId}
          className="mx-auto w-full max-w-[360px]"
          compact
          hideTips
          liveDictation={!detached?.preview}
          liveDictationComposerId={composerId}
          nativeSurface={false}
          onCompactExpansionDeltaChange={
            independentSurface ? undefined : handleCompactExpansionDeltaChange
          }
          value={draft}
          onChange={handleDraftChange}
          onSubmit={handlePendingSubmit}
          disabled={conversationReadOnly || !ctx}
          submitBlocked={creating}
          placeholder={
            !ctx
              ? t("connecting")
              : conversationReadOnly
                ? conversationReadOnlyReason
                : undefined
          }
          isStreaming={isRunning}
          onStop={onStop}
          agents={ctx?.inputAgents}
          selectedAgentIds={frameSelectedAgentIds}
          onSelectAgents={setFrameSelectedAgentIds}
          projectId={ctx?.projectId}
          spaceId={ctx?.spaceId}
          onOpenSettings={ctx?.onOpenSettings}
          onOpenSettingsForAgent={ctx?.onOpenSettingsForAgent}
          onRemoveAgent={ctx?.onRemoveAgent}
          images={ctx?.attachedImages}
          onRemoveImage={ctx?.onRemoveImage}
          onImageFile={ctx?.onImageFile}
          slashCommands={ctx?.slashCommands}
          maxParallelAgents={ctx?.maxParallelAgents}
          onChangeMaxParallelAgents={ctx?.onChangeMaxParallelAgents}
          onAgentInspect={ctx?.onAgentInspect}
          focusedAgent={focusedAgent}
          conversationController={conversationController}
          providers={ctx?.providers}
          onUpdateAgentModel={onUpdateAgentModel}
          onUpdateAgentEffort={onUpdateAgentEffort}
          companionDisconnected={ctx?.companionDisconnected}
          onFixCompanion={ctx?.onFixCompanion}
        />
      </div>
    </div>
  );
}
